import type {
  AskedQuestion,
  KnowledgeGapQuestion,
  KnowledgeGapsResult,
  TopicSignal,
} from "@/types/knowledge/knowledge-brain";
import type { Conversation } from "@/types/conversation/conversation";
import {
  detectTranscriptGaps,
  detectTranscriptQuestions,
  questionKey,
} from "./transcriptGaps";

/**
 * Rolls the per-conversation hedge detector up across every recent conversation
 * so the Knowledge Base can open directly on what the Genies failed to answer.
 *
 * Topic signals come from `conversation.details.topics`, which the existing
 * conversation analysis already produces — a topic seen only in conversations
 * that went cleanly is "confident"; one seen in a conversation the Genie hedged
 * in is "thin". No new backend, no invented data.
 */

export interface AggregateGapsOptions {
  /** agent id → display name, for labelling each failed question. */
  agentNames?: Record<string, string>;
  /** Max failed questions returned. Default 8. */
  limit?: number;
  /** Max gaps taken from any single conversation. Default 3. */
  perConversationLimit?: number;
  /** Max ranked asked questions returned. Default 24. */
  askedLimit?: number;
  /** Max questions counted from any single conversation. Default 8. */
  askedPerConversationLimit?: number;
}

const toIso = (conversation: Conversation): string | null => {
  const fromDetails = conversation.details?.conversation_date;
  if (fromDetails) return fromDetails;
  const startTime = conversation.metadata?.start_time;
  if (typeof startTime === "number" && Number.isFinite(startTime)) {
    return new Date(startTime * 1000).toISOString();
  }
  return null;
};

const normaliseTopic = (topic: unknown): string | null => {
  if (typeof topic !== "string") return null;
  const trimmed = topic.trim();
  if (trimmed.length < 2 || trimmed.length > 60) return null;
  return trimmed;
};

const rankTopics = (counts: Map<string, number>, limit: number): TopicSignal[] =>
  [...counts.entries()]
    .map(([label, mentions]) => ({ label, mentions }))
    .sort((a, b) => b.mentions - a.mentions || a.label.localeCompare(b.label))
    .slice(0, limit);

export const aggregateKnowledgeGaps = (
  conversations: Conversation[],
  options: AggregateGapsOptions = {},
): KnowledgeGapsResult => {
  const {
    agentNames = {},
    limit = 8,
    perConversationLimit = 3,
    askedLimit = 24,
    askedPerConversationLimit = 8,
  } = options;

  const byKey = new Map<string, KnowledgeGapQuestion>();
  const askedByKey = new Map<string, AskedQuestion>();
  const confidentCounts = new Map<string, number>();
  const thinCounts = new Map<string, number>();

  for (const conversation of conversations) {
    const gaps = detectTranscriptGaps(conversation.details?.transcript, perConversationLimit);
    const topics = Array.isArray(conversation.details?.topics)
      ? (conversation.details.topics as unknown[])
          .map(normaliseTopic)
          .filter((t): t is string => t !== null)
      : [];

    const target = gaps.length > 0 ? thinCounts : confidentCounts;
    for (const topic of topics) {
      target.set(topic, (target.get(topic) ?? 0) + 1);
    }

    // Every question, not just the fumbled ones — this is the "most asked"
    // signal. Occurrences roll up across conversations via the same loose key
    // the gap list uses, so the two rankings can never disagree on identity.
    const asked = detectTranscriptQuestions(
      conversation.details?.transcript,
      askedPerConversationLimit,
    );
    const askedAt = toIso(conversation);
    for (const entry of asked) {
      const key = questionKey(entry.question);
      if (!key) continue;
      const existing = askedByKey.get(key);
      if (existing) {
        existing.occurrences += 1;
        existing.hedged = existing.hedged || entry.hedged;
        if (askedAt && (!existing.lastAskedAt || askedAt > existing.lastAskedAt)) {
          existing.lastAskedAt = askedAt;
        }
        continue;
      }
      askedByKey.set(key, {
        id: `${conversation.id}:${entry.index}`,
        question: entry.question,
        occurrences: 1,
        hedged: entry.hedged,
        lastAskedAt: askedAt,
      });
    }

    for (const gap of gaps) {
      const key = questionKey(gap.question);
      const existing = byKey.get(key);
      if (existing) {
        existing.occurrences += 1;
        continue;
      }
      byKey.set(key, {
        id: `${conversation.id}:${gap.index}`,
        question: gap.question,
        agentReply: gap.agentReply,
        conversationId: conversation.id,
        agentId: conversation.agent_id,
        agentName: agentNames[conversation.agent_id] ?? null,
        occurredAt: toIso(conversation),
        occurrences: 1,
      });
    }
  }

  // A topic that shows up in both buckets is not confident — the Genie has
  // fumbled it at least once.
  for (const topic of thinCounts.keys()) confidentCounts.delete(topic);

  const failedQuestions = [...byKey.values()]
    .sort((a, b) => {
      if (b.occurrences !== a.occurrences) return b.occurrences - a.occurrences;
      const aAt = a.occurredAt ? Date.parse(a.occurredAt) : 0;
      const bAt = b.occurredAt ? Date.parse(b.occurredAt) : 0;
      return bAt - aAt;
    })
    .slice(0, limit);

  const askedQuestions = [...askedByKey.values()]
    .sort((a, b) => {
      if (b.occurrences !== a.occurrences) return b.occurrences - a.occurrences;
      const aAt = a.lastAskedAt ? Date.parse(a.lastAskedAt) : 0;
      const bAt = b.lastAskedAt ? Date.parse(b.lastAskedAt) : 0;
      return bAt - aAt;
    })
    .slice(0, askedLimit);

  return {
    failedQuestions,
    askedQuestions,
    confidentTopics: rankTopics(confidentCounts, 8),
    thinTopics: rankTopics(thinCounts, 8),
    conversationsScanned: conversations.length,
  };
};
